/**
 * Context Menu Hook - Action Handlers
 * 
 * Provides all context menu action implementations for BIM 2025 style menus.
 */

import { useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { useAppStore } from '../state/store';
import { tryRepeatLastCommand } from '../utils/commandCompletion';
import { useBIMStore } from '../state/useBIMStore';
import { useSketchStore } from '../state/useSketchStore';
import { useClipboardStore } from '../state/commandAuxStores';
import { ClipboardManager } from '../core/ClipboardManager';
import { captureSceneClipboard, pasteSceneClipboard, deleteSceneObjects } from '../core/scene';
import { familyLoader, familyEditor } from '../core/services';
import { generateId } from '../utils/helpers';
import { CadLinkStore } from '../services/cad/CadLinkStore';
import { readCadFile, applyParsedGeometryToElement, pickCadFile } from '../tools/LinkCADTool';
import { executeDraftCommand } from '../core/dynamicInput/dynamicInputEngine';
import i18n from '../i18n';

/** Dispatch a toast notification via the global NotificationHost. */
function notify(message: string, status: 'info' | 'success' | 'warning' | 'error' = 'info') {
    window.dispatchEvent(new CustomEvent('yap:notify', { detail: { message, status } }));
}

export interface ContextMenuActions {
    // Common Actions
    cancel: () => void;
    repeatLastCommand: () => void;

    // Clipboard Actions
    cut: () => void;
    copy: () => void;
    paste: () => void;
    delete: () => void;

    // Selection Actions
    selectAll: () => void;
    selectAllInstancesInView: () => void;
    selectAllInstancesInProject: () => void;
    filterSelection: () => void;

    // View Actions
    hideElements: () => void;
    hideCategory: () => void;
    isolateElement: () => void;
    isolateCategory: () => void;
    resetTemporaryHideIsolate: () => void;

    // Element Actions
    editType: () => void;
    editFamily: () => void;
    createSimilar: () => void;
    pin: () => void;
    unpin: () => void;
    flipFacing: () => void;
    flipHand: () => void;
    rotate: () => void;
    move: () => void;
    align: () => void;
    mirrorPick: () => void;
    trim: () => void;
    split: () => void;
    array: () => void;
    scale: () => void;
    overrideGraphicsByElement: () => void;
    overrideGraphicsByCategory: () => void;
    createGroup: () => void;

    // Navigation Actions
    zoomToFit: () => void;
    zoomToSelection: () => void;
    zoomInRegion: () => void;
    zoomOut: () => void;
    zoomSheetFit: () => void;

    // Properties
    showProperties: () => void;
    viewProperties: () => void;

    // Tool Activations
    activateWall: () => void;
    activateDoor: () => void;
    activateWindow: () => void;
    activateFloor: () => void;
    activateRoof: () => void;
    activateCeiling: () => void;
    activateStair: () => void;
    activateRailing: () => void;
    activateRamp: () => void;
    activateComponent: () => void;
    activateColumn: () => void;
    activateBeam: () => void;
    activateTruss: () => void;
    activateBrace: () => void;
    activateBeamSystem: () => void;
    activateIsolatedFoundation: () => void;
    activateWallFoundation: () => void;
    activateSlabFoundation: () => void;
    activateRebar: () => void;
    activateDuct: () => void;
    activatePipe: () => void;
    activateDuctFitting: () => void;
    activateDuctAccessory: () => void;
    activatePipeFitting: () => void;
    activatePipeAccessory: () => void;
    activateCableTrayFitting: () => void;
    activateWire: () => void;
    activateDevice: () => void;
    activateCableTray: () => void;
    activateConduit: () => void;
    activateAirTerminal: () => void;
    activateMechanicalEquipment: () => void;
    activatePlumbingFixture: () => void;
    activateSprinkler: () => void;
    activateLightingFixture: () => void;
    activateElectricalEquipment: () => void;
    activateFabricationPart: () => void;
    activateToposolid: () => void;
    activateDimension: () => void;
    activateDetailLine: () => void;
    activateText: () => void;
    activateTag: () => void;
    activateGrid: () => void;
    activateLevel: () => void;
    activateRoom: () => void;
    activateArea: () => void;
    activateSubRegion: () => void;
    activatePropertyLine: () => void;
    activateSteelConnection: () => void;
    activateSteelPlate: () => void;
    activateSteelBolts: () => void;
    activateSteelWelds: () => void;
    activateSteelModifier: () => void;
    activateSteelCope: () => void;
    activateSteelMiter: () => void;
    activateSteelCutThrough: () => void;
    activateAnalysisEnergy: () => void;
    activateMassingInPlace: () => void;
    activatePlaceMass: () => void;
    activateBuildingPad: () => void;
    activateSplitToposolid: () => void;
    activateMergeSurfaces: () => void;
    activateGradedRegion: () => void;
    activateSiteComponent: () => void;
    activateParkingComponent: () => void;
    activateCurtainPanelByFace: () => void;
    activateWallByFace: () => void;
    activateRoofByFace: () => void;
    activateFloorByFace: () => void;
    toggleShowMassFormAndFloors: () => void;
    activateViewSection: () => void;
    activateViewCallout: () => void;
    activateViewElevation: () => void;
    activateTagRoom: () => void;
    activateTagArea: () => void;
    activateTagSpace: () => void;
    activateTagMultiCategory: () => void;
    activateTagMaterial: () => void;
    activateKeynote: () => void;
    activateModelText: () => void;
    activateSwitchSystem: () => void;
    activateJoin: () => void;
    activateUnjoin: () => void;
    activateCutGeometry: () => void;
    activateUncutGeometry: () => void;
    activateLinework: () => void;

    // New Modify Tools
    activateOffset: () => void;
    activateMatchType: () => void;
    activateMirrorDraw: () => void;

    // Coordination / Insert extras
    activateDemolition: () => void;
    activateLinkPointCloud: () => void;
    activateManageImages: () => void;
    activateDecal: () => void;
    activateStructuralFloor: () => void;
    activateConvertToFabrication: () => void;
    activateSectionCreate: () => void;
    activateCurtainWall: () => void;

    // Insert tab — Link / Import / Load From Library / Manage Links commands
    activateLinkBim: () => void;
    activateLinkIFC: () => void;
    activateLinkCAD: () => void;
    activateLinkTopography: () => void;
    activateLinkPDFImage: () => void;
    activateLinkCoordinationModel: () => void;
    activateImportCAD: () => void;
    activateImportImage: () => void;
    activateImportPDF: () => void;
    activateLoadFamily: () => void;
    activateLoadCloudFamily: () => void;
    activateLoadGroup: () => void;
    activateManageLinks: () => void;

    // Structure tab — Structural Wall + Opening panel + datum/model additions
    activateStructuralWall: () => void;
    activateOpeningByFace: () => void;
    activateOpeningShaft: () => void;
    activateOpeningWall: () => void;
    activateOpeningVertical: () => void;
    activateOpeningCut: () => void;
    activateRebarCoupler: () => void;
    activateFabricSheet: () => void;
    activateModelInPlace: () => void;
    activateReferencePlane: () => void;

    // Commands (Non-Tools)
    executeSynchronize: () => void;
    executeProjectUnits: () => void;
    executeVisibilityGraphics: () => void;
    executeThinLines: () => void;
    executeLoadFamily: () => void;
    /** Re-parse the source file of a linked/imported CAD object in-place. */
    reloadLinkedCad: () => void;
}

export function useContextMenuActions(): ContextMenuActions {
    // We use getState() inside callbacks to ensure they are stable and don't trigger re-renders
    // or dependency changes when the store state updates.

    // Common Actions
    const cancel = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('select');
        appStore.clearSelection();
        bimStore.setActiveTool('SELECT');
    }, []);

    const repeatLastCommand = useCallback(() => {
        // Reuse the shared right-click repeat logic: re-arms store.lastUsedTool.
        tryRepeatLastCommand(false);
    }, []);

    // Clipboard Actions — the cross-workbench clipboard (B4): every citizen
    // is captured with its owning source and re-created inside its owner on
    // paste, so a mixed CAD/BIM/sketch selection survives the trip.
    const cut = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        const selectedIds = [...new Set([...appStore.selectedIds, ...bimStore.selection])];
        if (selectedIds.length === 0) return;

        const entries = captureSceneClipboard(selectedIds);
        if (entries.length > 0) {
            useClipboardStore.getState().copyCitizens(entries, [0, 0, 0]);
            // Keep the legacy BIM-only clipboard in sync for consumers that
            // still read it (PasteToolClass).
            const elements = selectedIds
                .map(id => bimStore.project.elements.get(id))
                .filter((el): el is NonNullable<typeof el> => Boolean(el));
            if (elements.length > 0) ClipboardManager.cut(elements);
        }
        deleteSceneObjects(selectedIds, 'Cut');
        appStore.clearSelection();
        bimStore.setSelection([]);
    }, []);

    const copy = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        const selectedIds = [...new Set([...appStore.selectedIds, ...bimStore.selection])];
        if (selectedIds.length === 0) return;

        const entries = captureSceneClipboard(selectedIds);
        if (entries.length === 0) return;
        useClipboardStore.getState().copyCitizens(entries, [0, 0, 0]);
        const elements = selectedIds
            .map(id => bimStore.project.elements.get(id))
            .filter((el): el is NonNullable<typeof el> => Boolean(el));
        if (elements.length > 0) ClipboardManager.copy(elements);
    }, []);

    const paste = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        const clipboard = useClipboardStore.getState();

        if (clipboard.entries.length > 0) {
            // Cross-workbench paste: each entry re-creates inside its owner
            // through the edit router (offset matches the legacy behavior).
            const matrix = new THREE.Matrix4().makeTranslation(1, 1, 0);
            const newIds = pasteSceneClipboard(clipboard.entries, matrix, 'Paste');
            if (newIds.length > 0) {
                appStore.select(newIds);
                bimStore.setSelection(newIds.filter((id) => bimStore.project.elements.has(id)));
            }
            return;
        }

        // Legacy fallback: BIM-only clipboard content (PasteToolClass's
        // source). Single write to the kernel store — the old path also
        // duplicated every element as a CAD entity.
        if (!ClipboardManager.hasContent) return;
        const newElements = ClipboardManager.paste({ x: 1, y: 1, z: 0 });
        newElements.forEach(el => {
            bimStore.addElement(el);
        });
        const newIds = newElements.map(e => e.id);
        appStore.select(newIds);
        bimStore.setSelection(newIds);
    }, []);

    const deleteSelected = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();

        const selectedIds = [...new Set([...appStore.selectedIds, ...bimStore.selection])];
        if (selectedIds.length === 0) return;
        // Linked CAD sources hang off the CAD entity payloads; drop them
        // before the routed delete removes the entities themselves. The
        // router splits the rest by owner (CAD/BIM/sketch), one undoable
        // step per store.
        selectedIds.forEach(id => {
            if (appStore.entities[id]) CadLinkStore.removeSource(id);
        });
        deleteSceneObjects(selectedIds, 'Delete');
        appStore.clearSelection();
        bimStore.setSelection([]);
    }, []);

    // Selection Actions
    const selectAll = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        const sketchStore = useSketchStore.getState();
        // Every workbench's citizens join the shared selection: CAD entities,
        // BIM elements, sketch solids and loose edges (the edit router and
        // the gumball partition them by owner on the next edit).
        const allIds = [
            ...Object.keys(appStore.entities),
            ...Array.from(bimStore.project.elements.keys()),
            ...Object.keys(sketchStore.solids),
            ...Object.keys(sketchStore.edges),
        ];
        appStore.select(allIds);
        bimStore.setSelection(allIds.filter((id) => bimStore.project.elements.has(id)));
    }, []);

    const selectAllInstancesInView = useCallback(() => {
        const appStore = useAppStore.getState();
        const selectedIds = appStore.selectedIds;
        if (selectedIds.length === 0) return;

        const entities = appStore.entities;
        const selectedEntity = entities[selectedIds[0]];
        if (!selectedEntity) return;

        // Find all entities of the same type
        const sameTypeIds = Object.values(entities)
            .filter(e => e.type === selectedEntity.type && e.visible)
            .map(e => e.id);

        appStore.select(sameTypeIds);
    }, []);

    const selectAllInstancesInProject = useCallback(() => {
        const appStore = useAppStore.getState();
        const selectedIds = appStore.selectedIds;
        if (selectedIds.length === 0) return;

        const entities = appStore.entities;
        const selectedEntity = entities[selectedIds[0]];
        if (!selectedEntity) return;

        // Find all entities of the same type (regardless of visibility)
        const sameTypeIds = Object.values(entities)
            .filter(e => e.type === selectedEntity.type)
            .map(e => e.id);

        appStore.select(sameTypeIds);
    }, []);

    const filterSelection = useCallback(() => {
        // BIM "Filter": narrow a multi-category selection down to one category.
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        const selectedIds = appStore.selectedIds;
        const bimSelection = bimStore.selection;

        if (selectedIds.length === 0 && bimSelection.length === 0) {
            notify(i18n.t('tools.notify.nothingSelectedToFilter'), 'warning');
            return;
        }

        // Prefer the entity selection when present; otherwise fall back to the
        // BIM element selection (context menu is shared across both stacks).
        if (selectedIds.length > 0) {
            const entities = appStore.entities;
            const counts = new Map<string, number>();
            selectedIds.forEach(id => {
                const t = entities[id]?.type;
                if (t) counts.set(t, (counts.get(t) ?? 0) + 1);
            });
            const dominant = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
            const kept = selectedIds.filter(id => entities[id]?.type === dominant);
            appStore.select(kept);
            notify(i18n.t('tools.notify.selectionFiltered', { count: kept.length, category: dominant }), 'success');
        } else {
            const elements = bimStore.project.elements;
            const counts = new Map<string, number>();
            bimSelection.forEach(id => {
                const c = elements.get(id)?.category;
                if (c) counts.set(c, (counts.get(c) ?? 0) + 1);
            });
            const dominant = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
            const kept = bimSelection.filter(id => elements.get(id)?.category === dominant);
            bimStore.setSelection(kept);
            notify(i18n.t('tools.notify.selectionFiltered', { count: kept.length, category: dominant }), 'success');
        }
    }, []);

    // View Actions
    const hideElements = useCallback(() => {
        // "Hide in View": view-scope, non-destructive (element data untouched).
        // In Reveal Hidden Elements mode the hidden elements stay visible in
        // the reveal highlight color.
        const appStore = useAppStore.getState();
        const selectedIds = appStore.selectedIds;
        if (selectedIds.length === 0) return;
        appStore.hideElementsInView(selectedIds);
        appStore.clearSelection();
        notify(i18n.t('tools.notify.elementsHidden', { count: selectedIds.length }), 'info');
    }, []);

    const hideCategory = useCallback(() => {
        // "Hide in View > Category": hide every element sharing the dominant
        // selection category (entity type for CAD, kernel category for BIM).
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        const selectedIds = appStore.selectedIds;
        const bimSelection = bimStore.selection;
        if (selectedIds.length === 0 && bimSelection.length === 0) return;

        const idsToHide: string[] = [];

        if (selectedIds.length > 0) {
            const category = appStore.entities[selectedIds[0]]?.type;
            if (!category) return;
            Object.values(appStore.entities)
                .filter(e => e.type === category)
                .forEach(e => idsToHide.push(e.id));
        } else {
            const category = bimStore.project.elements.get(bimSelection[0] ?? '')?.category;
            if (!category) return;
            bimStore.project.elements.forEach((el, id) => {
                if (el.category === category) idsToHide.push(id);
            });
        }

        if (idsToHide.length === 0) return;
        appStore.hideElementsInView(idsToHide);
        appStore.clearSelection();
        notify(i18n.t('tools.notify.categoryHidden', { count: idsToHide.length }), 'info');
    }, []);

    const isolateElement = useCallback(() => {
        // Temporary Isolate: view-scope only, non-destructive. The element
        // layer renders only the current selection until the mode is reset.
        const appStore = useAppStore.getState();
        if (appStore.selectedIds.length === 0) return;
        appStore.cycleHideIsolate('isolate');
        appStore.updateView({ isolateCategoryName: null });
        notify(i18n.t('tools.notify.elementsIsolated', { count: appStore.selectedIds.length }), 'info');
    }, []);

    const isolateCategory = useCallback(() => {
        // Temporary Isolate by category: every element sharing the dominant
        // selection category stays visible.
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        const selectedIds = appStore.selectedIds;
        const bimSelection = bimStore.selection;

        const category = selectedIds.length > 0
            ? appStore.entities[selectedIds[0]]?.type
            : bimStore.project.elements.get(bimSelection[0] ?? '')?.category;
        if (!category) return;

        appStore.updateView({ isolateCategoryName: category });
        appStore.cycleHideIsolate('isolate');
        notify(i18n.t('tools.notify.categoryIsolated', { category: category }), 'info');
    }, []);

    const resetTemporaryHideIsolate = useCallback(() => {
        // Restore visibility scope: clears isolate mode, the isolated category
        // and the "Hide in View" element list (BIM: Reset Temporary Hide/Isolate).
        useAppStore.getState().resetTemporaryHideIsolate();
        notify(i18n.t('tools.notify.temporaryHideIsolateReset'), 'success');
    }, []);

    // Element Actions
    const editType = useCallback(() => {
        // Open the BIM-style Type Properties dialog for the selected element.
        // Accepts both BIM elements (via either selection state) and falls back
        // to app entities' type string when no BIM element resolves.
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        const elementId = appStore.selectedIds[0] ?? bimStore.selection[0];
        if (!elementId) {
            notify(i18n.t('tools.notify.selectAnElementToEditIts'), 'warning');
            return;
        }
        const element =
            bimStore.project.elements.get(elementId) ??
            appStore.entities[elementId] ??
            null;
        const typeString: string = (element as any)?.type ?? '';
        const category: string = (element as any)?.category ?? 'Generic Models';
        if (!typeString) {
            notify(i18n.t('tools.notify.theSelectedObjectHasNoFamily'), 'warning');
            return;
        }
        const sep = typeString.indexOf(': ');
        const family = sep > 0 ? typeString.slice(0, sep) : typeString;
        const typeName = sep > 0 ? typeString.slice(sep + 2) : 'Default';
        appStore.openTypeProperties({
            family,
            type: typeName,
            category,
            elementIds: bimStore.project.elements.has(elementId) ? [elementId] : [],
        });
    }, []);

    const editFamily = useCallback(() => {
        // Enter the family editor for the selected element's family/type.
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        const selectedIds = appStore.selectedIds;
        const elementId = selectedIds[0] ?? bimStore.selection[0];
        if (!elementId) {
            notify(i18n.t('tools.notify.selectAFamilyInstanceToEdit'), 'warning');
            return;
        }
        const element =
            bimStore.project.elements.get(elementId) ??
            appStore.entities[elementId];
        const familyName =
            (element as any)?.type ?? (element as any)?.name ?? 'Unnamed Family';
        const category = (element as any)?.category ?? 'Generic Models';

        const family = familyEditor.createFamily(familyName, category);
        familyEditor.enterFamilyMode(family);
        notify(i18n.t('tools.notify.familyEditMode', { name: familyName, category: category }), 'success');
    }, []);

    const createSimilar = useCallback(() => {
        const appStore = useAppStore.getState();
        const selectedIds = appStore.selectedIds;
        if (selectedIds.length === 0) return;

        const entities = appStore.entities;
        const selectedEntity = entities[selectedIds[0]];
        if (!selectedEntity) return;

        // Activate the tool for the same type
        const toolMap: Record<string, any> = {
            'wall': 'wall',
            'door': 'door',
            'window': 'window',
            'column': 'column',
            'floor': 'floor',
            'line': 'line',
            'circle': 'circle',
            'rectangle': 'rectangle',
        };

        const tool = toolMap[selectedEntity.type];
        if (tool) {
            appStore.setActiveTool(tool);
        }
    }, []);

    const pin = useCallback(() => {
        const appStore = useAppStore.getState();
        const selectedIds = appStore.selectedIds;
        selectedIds.forEach(id => {
            appStore.updateEntity(id, { locked: true });
        });
    }, []);

    const unpin = useCallback(() => {
        const appStore = useAppStore.getState();
        const selectedIds = appStore.selectedIds;
        selectedIds.forEach(id => {
            appStore.updateEntity(id, { locked: false });
        });
    }, []);

    const flipFacing = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        const selectedIds = appStore.selectedIds;
        if (selectedIds.length === 0) return;

        selectedIds.forEach(id => {
            const el = bimStore.project.elements.get(id);
            if (el && (el.category === 'Doors' || el.category === 'Windows')) {
                const current = el.parameters['Flip Facing'] === true;
                bimStore.updateElement(id, {
                    parameters: { ...el.parameters, 'Flip Facing': !current },
                });
            }
        });
    }, []);

    const flipHand = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        const selectedIds = appStore.selectedIds;
        if (selectedIds.length === 0) return;

        selectedIds.forEach(id => {
            const el = bimStore.project.elements.get(id);
            if (el && (el.category === 'Doors' || el.category === 'Windows')) {
                const current = el.parameters['Flip Hand'] === true;
                bimStore.updateElement(id, {
                    parameters: { ...el.parameters, 'Flip Hand': !current },
                });
            }
        });
    }, []);

    const rotate = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        if (appStore.selectedIds.length === 0) return;
        appStore.setActiveTool('ROTATE');
        bimStore.setActiveTool('ROTATE');
    }, []);

    const move = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        if (appStore.selectedIds.length === 0) return;
        appStore.setActiveTool('MOVE');
        bimStore.setActiveTool('MOVE');
    }, []);

    const align = useCallback(() => {
        const appStore = useAppStore.getState();
        appStore.setActiveTool('ALIGN');
    }, []);

    const mirrorPick = useCallback(() => {
        const appStore = useAppStore.getState();
        appStore.setActiveTool('MIRROR_PICK');
    }, []);

    const trim = useCallback(() => {
        const appStore = useAppStore.getState();
        // Drafting shells have no TRIM class tool: route the command to the
        // CAD-entity runner instead of activating a dead tool id.
        if (appStore.mode === '2d' || appStore.mode === '3d') {
            executeDraftCommand('TRIM');
            return;
        }
        appStore.setActiveTool('TRIM');
    }, []);

    const split = useCallback(() => {
        const appStore = useAppStore.getState();
        appStore.setActiveTool('SPLIT');
    }, []);

    const array = useCallback(() => {
        const appStore = useAppStore.getState();
        if (appStore.mode === '2d' || appStore.mode === '3d') {
            executeDraftCommand('ARRAY');
            return;
        }
        appStore.setActiveTool('ARRAY');
    }, []);

    const scale = useCallback(() => {
        const appStore = useAppStore.getState();
        appStore.setActiveTool('SCALE');
    }, []);

    const overrideGraphicsByElement = useCallback(() => {
        // Per-element graphic override: lock the element to freeze it against
        // edits, and toggle its visibility as the override switch.
        const appStore = useAppStore.getState();
        const selectedIds = appStore.selectedIds;
        if (selectedIds.length === 0) {
            notify(i18n.t('tools.notify.selectElementSToOverride'), 'warning');
            return;
        }
        const anyUnlocked = selectedIds.some(id => !appStore.entities[id]?.locked);
        selectedIds.forEach(id => {
            appStore.updateEntity(id, { locked: anyUnlocked });
        });
        notify(i18n.t('tools.notify.elementOverride', { action: anyUnlocked ? i18n.t('tools.notify.actionApplied') : i18n.t('tools.notify.actionCleared'), count: selectedIds.length }), 'success');
    }, []);

    const overrideGraphicsByCategory = useCallback(() => {
        // Category-wide override: apply the same lock/visibility state to
        // every entity sharing the selection's dominant type.
        const appStore = useAppStore.getState();
        const selectedIds = appStore.selectedIds;
        if (selectedIds.length === 0) {
            notify(i18n.t('tools.notify.selectAnElementToDefineThe'), 'warning');
            return;
        }
        const entities = appStore.entities;
        const category = entities[selectedIds[0]]?.type;
        if (!category) return;
        const anyUnlocked = selectedIds.some(id => !entities[id]?.locked);
        const affected = Object.values(entities).filter(e => e.type === category);
        affected.forEach(e => {
            appStore.updateEntity(e.id, { locked: anyUnlocked });
        });
        notify(i18n.t('tools.notify.categoryOverride', { action: anyUnlocked ? i18n.t('tools.notify.actionApplied') : i18n.t('tools.notify.actionCleared'), count: affected.length, category: category }), 'success');
    }, []);

    const createGroup = useCallback(() => {
        // Create a named group from the current selection. Groups are backed
        // by a dedicated layer so they appear in the Layers panel and can be
        // toggled/locked together.
        const appStore = useAppStore.getState();
        const selectedIds = appStore.selectedIds;
        if (selectedIds.length === 0) {
            notify(i18n.t('tools.notify.selectElementSToCreateA'), 'warning');
            return;
        }
        const groupId = generateId();
        const groupCount = Object.keys(appStore.layers).filter(l => l.startsWith('group-')).length + 1;
        appStore.addLayer({
            id: groupId,
            name: `Group ${groupCount}`,
            color: '#4ECDC4',
            visible: true,
            locked: false,
            order: groupCount,
        });
        selectedIds.forEach(id => {
            appStore.updateEntity(id, { layer: groupId });
        });
        notify(i18n.t('tools.notify.groupCreated', { name: groupCount, count: selectedIds.length }), 'success');
    }, []);

    // Navigation Actions
    const zoomToFit = useCallback(() => {
        window.dispatchEvent(new CustomEvent('yap:zoom-to-fit'));
    }, []);

    const zoomToSelection = useCallback(() => {
        window.dispatchEvent(new CustomEvent('yap:zoom-to-selection'));
    }, []);

    const zoomInRegion = useCallback(() => {
        window.dispatchEvent(new CustomEvent('yap:zoom-to-selection'));
    }, []);

    const zoomOut = useCallback(() => {
        window.dispatchEvent(new CustomEvent('yap:zoom-out'));
    }, []);

    /** Frame the open sheet (BIM: Zoom Sheet Size). No-op when no sheet is open. */
    const zoomSheetFit = useCallback(() => {
        const bimStore = useBIMStore.getState();
        if (!bimStore.project.activeSheetId) return;
        window.dispatchEvent(new CustomEvent('yap:zoom-sheet-fit'));
    }, []);

    // Properties
    const showProperties = useCallback(() => {
        const appStore = useAppStore.getState();
        // Toggle properties panel
        appStore.togglePropertiesPanel();
    }, []);

    const viewProperties = useCallback(() => {
        // Show the active view's properties (type, scale, model display).
        const appStore = useAppStore.getState();
        const view = appStore.view;
        notify(i18n.t('tools.notify.viewProperties', { mode: view.viewMode, grid: view.gridVisible ? i18n.t('tools.notify.stateOn') : i18n.t('tools.notify.stateOff') }),
            'info'
        );
        // Ensure the Properties panel is visible so view settings are editable.
        if (!appStore.propertiesPanelOpen) {
            appStore.togglePropertiesPanel();
        }
    }, []);

    // Tool Activations
    const activateWall = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        // Architecture-tab Wall is explicitly non-structural, overriding any
        // structural mode previously set from the Structure tab.
        bimStore.setToolProperty('structuralWall', false);
        appStore.setActiveTool('wall');
        bimStore.setActiveTool('WALL');
    }, []);

    const activateDoor = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('door');
        bimStore.setActiveTool('DOOR');
    }, []);

    const activateWindow = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('window');
        bimStore.setActiveTool('WINDOW');
    }, []);

    const activateFloor = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('floor');
        bimStore.setActiveTool('FLOOR');
    }, []);

    const activateRoof = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('roof');
        bimStore.setActiveTool('ROOF');
    }, []);

    const activateCeiling = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('ceiling');
        bimStore.setActiveTool('CEILING');
    }, []);

    const activateStair = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('stair');
        bimStore.setActiveTool('STAIR');
    }, []);

    const activateRailing = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('railing');
        bimStore.setActiveTool('RAILING');
    }, []);

    const activateRamp = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('ramp');
        bimStore.setActiveTool('RAMP');
    }, []);

    const activateComponent = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('component');
        bimStore.setActiveTool('COMPONENT');
    }, []);

    const activateColumn = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('column');
        bimStore.setActiveTool('COLUMN');
    }, []);

    const activateBeam = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('beam');
        bimStore.setActiveTool('BEAM');
    }, []);

    const activateTruss = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('truss');
        bimStore.setActiveTool('TRUSS');
    }, []);

    const activateBrace = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('brace');
        bimStore.setActiveTool('BRACE');
    }, []);

    const activateBeamSystem = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('beam_system');
        bimStore.setActiveTool('BEAM_SYSTEM');
    }, []);

    const activateIsolatedFoundation = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('foundation_isolated');
        bimStore.setActiveTool('FOUNDATION_ISOLATED');
    }, []);

    const activateWallFoundation = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('foundation_wall');
        bimStore.setActiveTool('FOUNDATION_WALL');
    }, []);

    const activateSlabFoundation = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('foundation_slab');
        bimStore.setActiveTool('FOUNDATION_SLAB');
    }, []);

    const activateRebar = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('rebar');
        bimStore.setActiveTool('REBAR');
    }, []);

    const activateDuct = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('duct');
        bimStore.setActiveTool('DUCT');
    }, []);

    const activatePipe = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('pipe');
        bimStore.setActiveTool('PIPE');
    }, []);

    const activateCableTray = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('cable_tray');
        bimStore.setActiveTool('CABLE_TRAY');
    }, []);

    const activateConduit = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('conduit');
        bimStore.setActiveTool('CONDUIT');
    }, []);

    const activateAirTerminal = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('air_terminal');
        bimStore.setActiveTool('AIR_TERMINAL');
    }, []);

    const activateMechanicalEquipment = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('mechanical_equipment');
        bimStore.setActiveTool('MECHANICAL_EQUIPMENT');
    }, []);

    const activatePlumbingFixture = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('plumbing_fixture');
        bimStore.setActiveTool('PLUMBING_FIXTURE');
    }, []);

    const activateSprinkler = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('sprinkler');
        bimStore.setActiveTool('SPRINKLER');
    }, []);

    const activateLightingFixture = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('lighting_fixture');
        bimStore.setActiveTool('LIGHTING_FIXTURE');
    }, []);

    const activateElectricalEquipment = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('electrical_equipment');
        bimStore.setActiveTool('ELECTRICAL_EQUIPMENT');
    }, []);

    const activateFabricationPart = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('fabrication_part');
        bimStore.setActiveTool('FABRICATION_PART');
    }, []);

    // --- Systems tab: fittings, accessories, wire & device activations ---
    const activateDuctFitting = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('duct_fitting');
        bimStore.setActiveTool('DUCT_FITTING');
    }, []);

    const activateDuctAccessory = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('duct_accessory');
        bimStore.setActiveTool('DUCT_ACCESSORY');
    }, []);

    const activatePipeFitting = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('pipe_fitting');
        bimStore.setActiveTool('PIPE_FITTING');
    }, []);

    const activatePipeAccessory = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('pipe_accessory');
        bimStore.setActiveTool('PIPE_ACCESSORY');
    }, []);

    const activateCableTrayFitting = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('cable_tray_fitting');
        bimStore.setActiveTool('CABLE_TRAY_FITTING');
    }, []);

    const activateWire = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('wire');
        bimStore.setActiveTool('WIRE');
    }, []);

    const activateDevice = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('device');
        bimStore.setActiveTool('DEVICE');
    }, []);

    const activateToposolid = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('toposolid');
        bimStore.setActiveTool('TOPOSOLID');
    }, []);

    const activateDimension = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('dimension');
        bimStore.setActiveTool('DIMENSION');
    }, []);

    const activateDetailLine = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('detail_line');
        bimStore.setActiveTool('DETAIL_LINE');
    }, []);

    const activateText = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('text');
        bimStore.setActiveTool('TEXT');
    }, []);

    const activateTag = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('tag');
        bimStore.setActiveTool('TAG');
    }, []);

    const activateGrid = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('grid');
        bimStore.setActiveTool('GRID');
    }, []);

    const activateLevel = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('level');
        bimStore.setActiveTool('LEVEL');
    }, []);

    const activateRoom = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('room');
        bimStore.setActiveTool('ROOM');
    }, []);

    const activateArea = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('area');
        bimStore.setActiveTool('AREA');
    }, []);

    const activateSubRegion = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('subregion');
        bimStore.setActiveTool('SUBREGION');
    }, []);

    const activatePropertyLine = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('property_line');
        bimStore.setActiveTool('PROPERTY_LINE');
    }, []);

    const activateSteelConnection = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('steel_connection');
        bimStore.setActiveTool('STEEL_CONNECTION');
    }, []);

    // Steel tab: shared activator for the fabricate/cut tools. The app store
    // keeps the lowercase tool id, the BIM store the canonical uppercase key
    // that useToolController's registry resolves.
    const activateSteelTool = useCallback((id: string, key: string) => {
        useAppStore.getState().setActiveTool(id);
        useBIMStore.getState().setActiveTool(key);
    }, []);
    const activateSteelPlate = useCallback(() => activateSteelTool('steel_plate', 'STEEL_PLATE'), [activateSteelTool]);
    const activateSteelBolts = useCallback(() => activateSteelTool('steel_bolts', 'STEEL_BOLTS'), [activateSteelTool]);
    const activateSteelWelds = useCallback(() => activateSteelTool('steel_welds', 'STEEL_WELDS'), [activateSteelTool]);
    const activateSteelModifier = useCallback(() => activateSteelTool('steel_modifier', 'STEEL_MODIFIER'), [activateSteelTool]);
    const activateSteelCope = useCallback(() => activateSteelTool('steel_cope', 'STEEL_COPE'), [activateSteelTool]);
    const activateSteelMiter = useCallback(() => activateSteelTool('steel_miter', 'STEEL_MITER'), [activateSteelTool]);
    const activateSteelCutThrough = useCallback(() => activateSteelTool('steel_cut_through', 'STEEL_CUT_THROUGH'), [activateSteelTool]);

    const activateAnalysisEnergy = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('analysis_energy');
        bimStore.setActiveTool('ANALYSIS_ENERGY');
    }, []);

    const activateMassingInPlace = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('in_place_mass');
        bimStore.setActiveTool('IN_PLACE_MASS');
    }, []);

    // Massing & Site — shared activator: the app store keeps the lowercase
    // id the tool controller resolves, the BIM store the canonical uppercase.
    const activateMassingTool = useCallback((id: string, key: string) => {
        useAppStore.getState().setActiveTool(id);
        useBIMStore.getState().setActiveTool(key);
    }, []);
    const activatePlaceMass = useCallback(() => activateMassingTool('place_mass', 'PLACE_MASS'), [activateMassingTool]);
    const activateBuildingPad = useCallback(() => activateMassingTool('building_pad', 'BUILDING_PAD'), [activateMassingTool]);
    const activateSplitToposolid = useCallback(() => activateMassingTool('split_toposolid', 'SPLIT_TOPOSOLID'), [activateMassingTool]);
    const activateMergeSurfaces = useCallback(() => activateMassingTool('merge_surfaces', 'MERGE_SURFACES'), [activateMassingTool]);
    const activateGradedRegion = useCallback(() => activateMassingTool('graded_region', 'GRADED_REGION'), [activateMassingTool]);
    const activateSiteComponent = useCallback(() => activateMassingTool('site_component', 'SITE_COMPONENT'), [activateMassingTool]);
    const activateParkingComponent = useCallback(() => activateMassingTool('parking_component', 'PARKING_COMPONENT'), [activateMassingTool]);
    const activateCurtainPanelByFace = useCallback(() => activateMassingTool('curtain_panel_by_face', 'CURTAIN_PANEL_BY_FACE'), [activateMassingTool]);
    const activateWallByFace = useCallback(() => activateMassingTool('wall_by_face', 'WALL_BY_FACE'), [activateMassingTool]);
    const activateRoofByFace = useCallback(() => activateMassingTool('roof_by_face', 'ROOF_BY_FACE'), [activateMassingTool]);
    const activateFloorByFace = useCallback(() => activateMassingTool('floor_by_face', 'FLOOR_BY_FACE'), [activateMassingTool]);
    const toggleShowMassFormAndFloors = useCallback(() => {
        const appStore = useAppStore.getState();
        appStore.updateView({ showMassFormAndFloors: !appStore.view.showMassFormAndFloors });
    }, []);

    const activateViewSection = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('view_section');
        bimStore.setActiveTool('VIEW_SECTION');
    }, []);

    const activateViewCallout = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('view_callout');
        bimStore.setActiveTool('VIEW_CALLOUT');
    }, []);

    const activateViewElevation = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('view_elevation');
        bimStore.setActiveTool('VIEW_ELEVATION');
    }, []);

    const activateTagRoom = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('tag_room');
        bimStore.setActiveTool('TAG_ROOM');
    }, []);

    const activateTagArea = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('tag_area');
        bimStore.setActiveTool('TAG_AREA');
    }, []);

    const activateTagSpace = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('tag_space');
        bimStore.setActiveTool('TAG_SPACE');
    }, []);

    const activateTagMultiCategory = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('tag_multi_category');
        bimStore.setActiveTool('TAG_MULTI_CATEGORY');
    }, []);

    const activateTagMaterial = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('tag_material');
        bimStore.setActiveTool('TAG_MATERIAL');
    }, []);

    const activateKeynote = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('keynote');
        bimStore.setActiveTool('KEYNOTE');
    }, []);

    const activateModelText = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('model_text');
        bimStore.setActiveTool('MODEL_TEXT');
    }, []);

    const activateSwitchSystem = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('switch_system');
        bimStore.setActiveTool('SWITCH_SYSTEM');
    }, []);

    const activateJoin = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('join');
        bimStore.setActiveTool('JOIN');
    }, []);

    const activateUnjoin = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('unjoin');
        bimStore.setActiveTool('UNJOIN');
    }, []);

    const activateCutGeometry = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('cut_geometry');
        bimStore.setActiveTool('CUT_GEOMETRY');
    }, []);

    const activateUncutGeometry = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('uncut_geometry');
        bimStore.setActiveTool('UNCUT_GEOMETRY');
    }, []);

    const activateLinework = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('linework');
        bimStore.setActiveTool('LINEWORK');
    }, []);

    // New Modify Tools
    const activateOffset = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('offset');
        bimStore.setActiveTool('OFFSET');
    }, []);

    const activateMatchType = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('match_type');
        bimStore.setActiveTool('MATCH_TYPE');
    }, []);

    const activateMirrorDraw = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('mirror_draw');
        bimStore.setActiveTool('MIRROR_DRAW');
    }, []);

    // Coordination / Insert extras
    const activateDemolition = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('demolition');
        bimStore.setActiveTool('DEMOLITION');
    }, []);

    const activateLinkPointCloud = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('link_point_cloud');
        bimStore.setActiveTool('LINK_POINT_CLOUD');
    }, []);

    const activateManageImages = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('manage_images');
        bimStore.setActiveTool('MANAGE_IMAGES');
    }, []);

    const activateDecal = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('decal');
        bimStore.setActiveTool('DECAL');
    }, []);

    // Insert tab tools: each action activates the dialog-driven tool that
    // owns the flow (file picker, link registration, Manage Links dialog).
    const insertTool = (appTool: string, bimTool: string) => {
        useAppStore.getState().setActiveTool(appTool);
        useBIMStore.getState().setActiveTool(bimTool);
    };

    const activateLinkBim = useCallback(() => insertTool('link_bim', 'LINK_BIM'), []);
    const activateLinkIFC = useCallback(() => insertTool('link_ifc', 'LINK_IFC'), []);
    const activateLinkCAD = useCallback(() => insertTool('link_cad', 'LINK_CAD'), []);
    const activateLinkTopography = useCallback(() => insertTool('link_topography', 'LINK_TOPOGRAPHY'), []);
    const activateLinkPDFImage = useCallback(() => insertTool('link_pdf_image', 'LINK_PDF_IMAGE'), []);
    const activateLinkCoordinationModel = useCallback(() => insertTool('link_coordination_model', 'LINK_COORDINATION_MODEL'), []);
    const activateImportCAD = useCallback(() => insertTool('import_cad', 'IMPORT_CAD'), []);
    const activateImportImage = useCallback(() => insertTool('import_image', 'IMPORT_IMAGE'), []);
    const activateImportPDF = useCallback(() => insertTool('import_pdf', 'IMPORT_PDF'), []);
    const activateLoadFamily = useCallback(() => insertTool('load_family', 'LOAD_FAMILY'), []);
    const activateLoadCloudFamily = useCallback(() => insertTool('load_cloud_family', 'LOAD_CLOUD_FAMILY'), []);
    const activateLoadGroup = useCallback(() => insertTool('load_group', 'LOAD_GROUP'), []);
    const activateManageLinks = useCallback(() => insertTool('manage_links', 'MANAGE_LINKS'), []);

    const activateStructuralFloor = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('structural_floor');
        bimStore.setActiveTool('STRUCTURAL_FLOOR');
    }, []);

    const activateConvertToFabrication = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('convert_to_fabrication');
        bimStore.setActiveTool('CONVERT_TO_FABRICATION');
    }, []);

    const activateSectionCreate = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('view_section_create');
        bimStore.setActiveTool('VIEW_SECTION_CREATE');
    }, []);

    const activateCurtainWall = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('curtain_wall');
        bimStore.setActiveTool('CURTAIN_WALL');
    }, []);

    // Structure tab — Structural Wall reuses the wall geometry pipeline with
    // the structural flag pinned on (BIM WA vs Structure-tab Wall parity).
    const activateStructuralWall = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        bimStore.setToolProperty('structuralWall', true);
        appStore.setActiveTool('wall');
        bimStore.setActiveTool('WALL');
    }, []);

    const activateOpeningByFace = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('opening_by_face');
        bimStore.setActiveTool('OPENING_BY_FACE');
    }, []);

    const activateOpeningShaft = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('opening_shaft');
        bimStore.setActiveTool('OPENING_SHAFT');
    }, []);

    const activateOpeningWall = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('opening_wall');
        bimStore.setActiveTool('OPENING_WALL');
    }, []);

    const activateOpeningVertical = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('opening_vertical');
        bimStore.setActiveTool('OPENING_VERTICAL');
    }, []);

    const activateOpeningCut = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('opening_cut');
        bimStore.setActiveTool('OPENING_CUT');
    }, []);

    const activateRebarCoupler = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('rebar_coupler');
        bimStore.setActiveTool('REBAR_COUPLER');
    }, []);

    const activateFabricSheet = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('fabric_sheet');
        bimStore.setActiveTool('FABRIC_SHEET');
    }, []);

    const activateModelInPlace = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('model_in_place');
        bimStore.setActiveTool('MODEL_IN_PLACE');
    }, []);

    const activateReferencePlane = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('reference_plane');
        bimStore.setActiveTool('REFERENCE_PLANE');
    }, []);

    // Commands (Non-Tools)
    const executeSynchronize = useCallback(() => {
        const bimStore = useBIMStore.getState();
        bimStore.saveProject().then((json) => {
            window.localStorage.setItem('yap-central-model', json);
            window.dispatchEvent(
                new CustomEvent('yap:notify', {
                    detail: { message: 'Synchronized with central model (local snapshot saved).', status: 'success' },
                })
            );
        });
    }, []);

    const executeProjectUnits = useCallback(() => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();
        appStore.setActiveTool('manage_units');
        bimStore.setActiveTool('MANAGE_UNITS');
    }, []);

    const executeVisibilityGraphics = useCallback(() => {
        // Open the full Visibility/Graphics dialog (View tab > Graphics):
        // per-category visibility + filters persisted on the active view.
        useAppStore.getState().openManageDialog('visibilityGraphics');
    }, []);

    const executeThinLines = useCallback(() => {
        const appStore = useAppStore.getState();
        appStore.toggleViewFlag('thinLines');
        window.dispatchEvent(
            new CustomEvent('yap:notify', {
                detail: { message: `Thin Lines ${appStore.view.thinLines ? 'on' : 'off'}.` },
            })
        );
    }, []);

    const executeLoadFamily = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json,.txt';
        input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;
            file.text().then((content) => {
                try {
                    familyLoader.loadFamily(JSON.parse(content)).then(() => {
                        window.dispatchEvent(
                            new CustomEvent('yap:notify', {
                                detail: { message: `Family "${file.name}" loaded.`, status: 'success' },
                            })
                        );
                    });
                } catch (err) {
                    window.dispatchEvent(
                        new CustomEvent('yap:notify', {
                            detail: { message: `Failed to load family: ${String(err)}`, status: 'error' },
                        })
                    );
                }
            });
        };
        input.click();
    }, []);

    /**
     * Reload a linked/imported CAD object: re-read the remembered source file
     * and rebuild its line work in place (2D-style XREF reload). When the
     * session file handle is gone, ask for the file again first.
     */
    const reloadLinkedCad = useCallback(async () => {
        const appStore = useAppStore.getState();
        const bimStore = useBIMStore.getState();

        const ids = [...new Set([...appStore.selectedIds, ...bimStore.selection])];
        const cadElement = ids
            .map(id => bimStore.project.elements.get(id) as any)
            .find((el: any) => el && (el.category === 'CAD Links' || el.type === 'Imported CAD'));

        if (!cadElement) {
            notify(i18n.t('tools.notify.selectALinkedOrImportedCad'), 'warning');
            return;
        }

        let file = CadLinkStore.getSource(cadElement.id)?.file ?? null;
        if (!file) {
            // Session handle lost (app was restarted): the user points at the
            // same file on disk to re-bind the source.
            file = await pickCadFile();
            if (!file) {
                notify(i18n.t('tools.notify.reloadCancelledNoFileSelected'), 'info');
                return;
            }
        }

        try {
            const result = await readCadFile(file);
            applyParsedGeometryToElement(cadElement.id, file, result, bimStore);
            CadLinkStore.markLoaded(cadElement.id);
            notify(i18n.t('tools.notify.cadReloaded', { fileName: file.name, count: result.lines.length }), 'success');
        } catch (err) {
            notify(i18n.t('tools.notify.cadReloadFailed', { message: (err as Error).message }), 'error');
        }
    }, []);

    // Memoize the return object so it's referentially stable
    return useMemo(() => ({
        cancel,
        repeatLastCommand,
        cut,
        copy,
        paste,
        delete: deleteSelected,
        selectAll,
        selectAllInstancesInView,
        selectAllInstancesInProject,
        filterSelection,
        hideElements,
        hideCategory,
        isolateElement,
        isolateCategory,
        resetTemporaryHideIsolate,
        editType,
        editFamily,
        createSimilar,
        pin,
        unpin,
        flipFacing,
        flipHand,
        rotate,
        move,
        align,
        mirrorPick,
        trim,
        split,
        array,
        scale,
        overrideGraphicsByElement,
        overrideGraphicsByCategory,
        createGroup,
        zoomToFit,
        zoomToSelection,
        zoomInRegion,
        zoomOut,
        zoomSheetFit,
        showProperties,
        viewProperties,
        activateWall,
        activateDoor,
        activateWindow,
        activateFloor,
        activateRoof,
        activateCeiling,
        activateStair,
        activateRailing,
        activateRamp,
        activateComponent,
        activateColumn,
        activateBeam,
        activateTruss,
        activateBrace,
        activateBeamSystem,
        activateIsolatedFoundation,
        activateWallFoundation,
        activateSlabFoundation,
        activateRebar,
        activateDuct,
        activatePipe,
        activateDuctFitting,
        activateDuctAccessory,
        activatePipeFitting,
        activatePipeAccessory,
        activateCableTrayFitting,
        activateWire,
        activateDevice,
        activateCableTray,
        activateConduit,
        activateAirTerminal,
        activateMechanicalEquipment,
        activatePlumbingFixture,
        activateSprinkler,
        activateLightingFixture,
        activateElectricalEquipment,
        activateFabricationPart,
        activateToposolid,
        activateDimension,
        activateDetailLine,
        activateText,
        activateTag,
        activateGrid,
        activateLevel,
        activateRoom,
        activateArea,
        activateSubRegion,
        activatePropertyLine,
        activateSteelConnection,
        activateSteelPlate,
        activateSteelBolts,
        activateSteelWelds,
        activateSteelModifier,
        activateSteelCope,
        activateSteelMiter,
        activateSteelCutThrough,
        activateAnalysisEnergy,
        activateMassingInPlace,
        activatePlaceMass,
        activateBuildingPad,
        activateSplitToposolid,
        activateMergeSurfaces,
        activateGradedRegion,
        activateSiteComponent,
        activateParkingComponent,
        activateCurtainPanelByFace,
        activateWallByFace,
        activateRoofByFace,
        activateFloorByFace,
        toggleShowMassFormAndFloors,
        activateViewSection,
        activateViewCallout,
        activateViewElevation,
        activateTagRoom,
        activateTagArea,
        activateTagSpace,
        activateTagMultiCategory,
        activateTagMaterial,
        activateKeynote,
        activateModelText,
        activateSwitchSystem,
        activateJoin,
        activateUnjoin,
        activateCutGeometry,
        activateUncutGeometry,
        activateLinework,
        activateOffset,
        activateMatchType,
        activateMirrorDraw,
        activateDemolition,
        activateLinkPointCloud,
        activateManageImages,
        activateDecal,
        activateStructuralFloor,
        activateConvertToFabrication,
        activateSectionCreate,
        activateCurtainWall,
        activateLinkBim,
        activateLinkIFC,
        activateLinkCAD,
        activateLinkTopography,
        activateLinkPDFImage,
        activateLinkCoordinationModel,
        activateImportCAD,
        activateImportImage,
        activateImportPDF,
        activateLoadFamily,
        activateLoadCloudFamily,
        activateLoadGroup,
        activateManageLinks,
        activateStructuralWall,
        activateOpeningByFace,
        activateOpeningShaft,
        activateOpeningWall,
        activateOpeningVertical,
        activateOpeningCut,
        activateRebarCoupler,
        activateFabricSheet,
        activateModelInPlace,
        activateReferencePlane,
        executeSynchronize,
        executeProjectUnits,
        executeVisibilityGraphics,
        executeThinLines,
        executeLoadFamily,
        reloadLinkedCad,
    }), [
        cancel,
        repeatLastCommand,
        cut,
        copy,
        paste,
        deleteSelected,
        selectAll,
        selectAllInstancesInView,
        selectAllInstancesInProject,
        filterSelection,
        hideElements,
        hideCategory,
        isolateElement,
        isolateCategory,
        resetTemporaryHideIsolate,
        editType,
        editFamily,
        createSimilar,
        pin,
        unpin,
        flipFacing,
        flipHand,
        rotate,
        move,
        align,
        mirrorPick,
        trim,
        split,
        array,
        scale,
        overrideGraphicsByElement,
        overrideGraphicsByCategory,
        createGroup,
        zoomToFit,
        zoomToSelection,
        zoomInRegion,
        zoomOut,
        zoomSheetFit,
        showProperties,
        viewProperties,
        activateWall,
        activateDoor,
        activateWindow,
        activateFloor,
        activateRoof,
        activateCeiling,
        activateStair,
        activateRailing,
        activateRamp,
        activateComponent,
        activateColumn,
        activateBeam,
        activateTruss,
        activateBrace,
        activateBeamSystem,
        activateIsolatedFoundation,
        activateWallFoundation,
        activateSlabFoundation,
        activateRebar,
        activateDuct,
        activatePipe,
        activateDuctFitting,
        activateDuctAccessory,
        activatePipeFitting,
        activatePipeAccessory,
        activateCableTrayFitting,
        activateWire,
        activateDevice,
        activateCableTray,
        activateConduit,
        activateAirTerminal,
        activateMechanicalEquipment,
        activatePlumbingFixture,
        activateSprinkler,
        activateLightingFixture,
        activateElectricalEquipment,
        activateFabricationPart,
        activateToposolid,
        activateDimension,
        activateDetailLine,
        activateText,
        activateTag,
        activateGrid,
        activateLevel,
        activateRoom,
        activateArea,
        activateSubRegion,
        activatePropertyLine,
        activateSteelConnection,
        activateSteelPlate,
        activateSteelBolts,
        activateSteelWelds,
        activateSteelModifier,
        activateSteelCope,
        activateSteelMiter,
        activateSteelCutThrough,
        activateAnalysisEnergy,
        activateMassingInPlace,
        activatePlaceMass,
        activateBuildingPad,
        activateSplitToposolid,
        activateMergeSurfaces,
        activateGradedRegion,
        activateSiteComponent,
        activateParkingComponent,
        activateCurtainPanelByFace,
        activateWallByFace,
        activateRoofByFace,
        activateFloorByFace,
        toggleShowMassFormAndFloors,
        activateViewSection,
        activateViewCallout,
        activateViewElevation,
        activateTagRoom,
        activateTagArea,
        activateTagSpace,
        activateTagMultiCategory,
        activateTagMaterial,
        activateKeynote,
        activateModelText,
        activateSwitchSystem,
        activateJoin,
        activateUnjoin,
        activateCutGeometry,
        activateUncutGeometry,
        activateLinework,
        activateOffset,
        activateMatchType,
        activateMirrorDraw,
        activateDemolition,
        activateLinkPointCloud,
        activateManageImages,
        activateDecal,
        activateStructuralFloor,
        activateConvertToFabrication,
        activateSectionCreate,
        activateCurtainWall,
        activateLinkBim,
        activateLinkIFC,
        activateLinkCAD,
        activateLinkTopography,
        activateLinkPDFImage,
        activateLinkCoordinationModel,
        activateImportCAD,
        activateImportImage,
        activateImportPDF,
        activateLoadFamily,
        activateLoadCloudFamily,
        activateLoadGroup,
        activateManageLinks,
        activateStructuralWall,
        activateOpeningByFace,
        activateOpeningShaft,
        activateOpeningWall,
        activateOpeningVertical,
        activateOpeningCut,
        activateRebarCoupler,
        activateFabricSheet,
        activateModelInPlace,
        activateReferencePlane,
        executeSynchronize,
        executeProjectUnits,
        executeVisibilityGraphics,
        executeThinLines,
        executeLoadFamily,
        reloadLinkedCad,
    ]);
}
